import { styled } from '@mui/material';
import { ReactFlow } from '@xyflow/react';
import { Node as ReactFlowNode } from '@xyflow/react';

import { REACT_FLOW_CLASSES, REACT_FLOW_ZINDEX } from '../constants';
import { ExternalFlowChartProps, InternalFlowChartProps, Node } from '../types';

type ThemedReactFlowStyleProps = Pick<
  ExternalFlowChartProps,
  | 'presentationMode'
  | 'hideEdges'
  | 'edgeCursorPointer'
  | 'edgeLabelsBelowNodes'
>;

const EDGE_LABEL_TARGET = `* .${REACT_FLOW_CLASSES.EDGE_LABEL}`;
const EDGE = `.${REACT_FLOW_CLASSES.EDGE}`;
const PANE = `.${REACT_FLOW_CLASSES.PANE}`;
const PANE_SELECTION = `${PANE}.selection`;
const NODE = `.${REACT_FLOW_CLASSES.NODE}`;
const DRAGGABLE_NODE = `${NODE}.draggable`;
const SELECTED_DRAGGABLE_NODE = `${DRAGGABLE_NODE}.selected`;
const DRAGGING_NODE = `${DRAGGABLE_NODE}.dragging`;

const presentationModeStyles = {
  [PANE]: {
    cursor: 'default',
  },
  [NODE]: {
    cursor: 'default',
  },
};

const hideEdgesStyles = {
  [EDGE]: {
    display: 'none',
  },
};

// Create the styled component that accepts the style props
const StyledReactFlow = styled(ReactFlow, {
  // React Flow forwards unknown props to its root DOM element.
  shouldForwardProp: (prop) =>
    ![
      'presentationMode',
      'hideEdges',
      'edgeCursorPointer',
      'edgeLabelsBelowNodes',
      'ownerState',
      'theme',
      'sx',
      'as',
    ].includes(String(prop)),
})<ThemedReactFlowStyleProps>(
  ({
    presentationMode,
    hideEdges,
    edgeCursorPointer = true,
    edgeLabelsBelowNodes = false,
  }) => ({
    // By default, force edge labels above nodes and other lines.
    // When edgeLabelsBelowNodes is true, drop the override so labels paint
    // in DOM tree order (between edges and nodes), letting nodes occlude
    // overlapping labels.
    [EDGE_LABEL_TARGET]: {
      zIndex: edgeLabelsBelowNodes
        ? 'auto'
        : REACT_FLOW_ZINDEX.EDGE_LABEL_RENDERER,
    },
    [EDGE]: {
      cursor: edgeCursorPointer ? 'pointer' : 'default',
    },
    // Edit mode marks nodes `.draggable`. React Flow defaults those to grab;
    // keep the default arrow on hover, and use move once a node is selected
    // (one or many) or while it is being dragged.
    [DRAGGABLE_NODE]: {
      cursor: 'default',
    },
    [SELECTED_DRAGGABLE_NODE]: {
      cursor: 'move',
    },
    [DRAGGING_NODE]: {
      cursor: 'move',
    },
    // Holding Shift (React Flow's selection key) adds `.selection` to the
    // pane for marquee-select. Use the default arrow instead of pointer.
    [PANE_SELECTION]: {
      cursor: 'default',
    },
    ...(presentationMode ? presentationModeStyles : {}),
    ...(hideEdges ? hideEdgesStyles : {}),
  })
);

export function ThemedReactFlow<T extends ReactFlowNode = Node>(
  props: InternalFlowChartProps<T> & ThemedReactFlowStyleProps
) {
  // Extract the style-specific props for the styled component
  const {
    presentationMode,
    hideEdges,
    edgeCursorPointer,
    edgeLabelsBelowNodes,
    ...reactFlowProps
  } = props;

  // We need to cast the entire props object to avoid TypeScript's strict variance checking
  // This is safe because:
  // 1. T extends ReactFlowNode, so all node-related props are compatible
  // 2. The styled component just passes props through to ReactFlow
  // 3. ReactFlow will handle the actual typing at runtime
  const styledProps = {
    presentationMode,
    hideEdges,
    edgeCursorPointer,
    edgeLabelsBelowNodes,
    ...reactFlowProps,
  } as Parameters<typeof StyledReactFlow>[0];

  return <StyledReactFlow {...styledProps} />;
}
